import React, { useRef, useState, useEffect, useMemo } from 'react'
import { Box, Typography, IconButton, Paper } from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CloseIcon from '@mui/icons-material/Close'
import CustomModal from '../Modal'
import { Button, DialogActions } from '@material-ui/core'

/**
 * Detect file type from URL
 */
const getFileTypeFromUrl = (url) => {
  const cleanUrl = url.split('?')[0].toLowerCase()

  if (/\.(jpg|jpeg|png|gif|webp|jfif)$/.test(cleanUrl)) return 'image'
  if (cleanUrl.endsWith('.pdf')) return 'pdf'
  return 'other'
}

export default function FileUpload({
  multiple = false,
  value = multiple ? [] : null,
  onChange,
  accept = 'image/*,application/pdf',
  maxSizeMb = 5,
  maxFiles = 5,
  viewOnly = false,
  onFilesAdded,
  onRemove,
  compactMode = false,
  previewWidth = 120,
}) {
  const inputRef = useRef(null)
  const [previews, setPreviews] = useState([])
  const [error, setError] = useState(null)

  const [previewOpen, setPreviewOpen] = useState(false)
  const [activePreview, setActivePreview] = useState(null)

  const openPreview = (preview) => {
    if (!viewOnly) return
    setActivePreview(preview)
    setPreviewOpen(true)
  }

  const closePreview = () => {
    setPreviewOpen(false)
    setActivePreview(null)
  }

  /**
   * Normalize value into array
   */
  const itemsArray = useMemo(() => {
    if (!value) return []
    return Array.isArray(value) ? value : [value]
  }, [value])

  /**
   * Generate previews
   */
  useEffect(() => {
    if (!itemsArray.length) {
      setPreviews([])
      return
    }

    const mapped = itemsArray.map((item) => {
      // Remote string URL
      if (typeof item === 'string') {
        return {
          file: null,
          url: item,
          type: getFileTypeFromUrl(item),
          name: item.split('/').pop().split('?')[0],
          isRemote: true,
        }
      }

      // Remote object { key, url }
      if (typeof item === 'object' && item?.url) {
        return {
          file: null,
          url: item.url,
          type: getFileTypeFromUrl(item.url),
          name: item.key || item.url.split('/').pop().split('?')[0],
          isRemote: true,
          raw: item,
        }
      }

      // Local File object
      const type = item.type?.startsWith('image/')
        ? 'image'
        : item.type === 'application/pdf'
        ? 'pdf'
        : 'other'

      return {
        file: item,
        url: URL.createObjectURL(item),
        type,
        name: item.name,
        isRemote: false,
      }
    })

    setPreviews(mapped)

    return () => {
      mapped.forEach((p) => {
        if (!p.isRemote && p.url?.startsWith('blob:')) {
          URL.revokeObjectURL(p.url)
        }
      })
    }
  }, [itemsArray])

  const validateFile = (file) => {
    if (file.size > maxSizeMb * 1024 * 1024) {
      return `File "${file.name}" exceeds ${maxSizeMb}MB`
    }
    return null
  }

  const handleFiles = (fileList) => {
    if (viewOnly) return
    setError(null)

    const incoming = Array.from(fileList)

    if (multiple && itemsArray.length + incoming.length > maxFiles) {
      setError(`You can only upload up to ${maxFiles} files`)
      return
    }

    for (const file of incoming) {
      const err = validateFile(file)
      if (err) {
        setError(err)
        return
      }
    }

    onFilesAdded?.(multiple ? incoming : incoming[0])

    if (multiple) {
      onChange?.([...itemsArray, ...incoming])
    } else {
      onChange?.(incoming[0])
    }
  }

  const removeFile = (index) => {
    if (viewOnly) return

    if (multiple) {
      const updated = [...itemsArray]
      updated.splice(index, 1)
      onChange?.(updated)
    } else {
      onChange?.(null)
    }
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        textAlign: 'center',
        borderStyle: viewOnly ? 'solid' : 'dashed',
        cursor: viewOnly ? 'default' : 'pointer',
      }}
      onClick={() => !viewOnly && inputRef.current?.click()}
      onDragOver={(e) => !viewOnly && e.preventDefault()}
      onDrop={(e) => {
        if (viewOnly) return
        e.preventDefault()
        if (e.dataTransfer.files?.length) {
          handleFiles(e.dataTransfer.files)
        }
      }}
    >
      {!viewOnly && (
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple={multiple}
          accept={accept}
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
      )}

      {!itemsArray.length && !viewOnly && (
        <>
          <UploadFileIcon fontSize="large" />
          {!compactMode && (
            <>
              <Typography variant="body2" mt={1}>
                {multiple
                  ? 'Click or drag files to upload'
                  : 'Click or drag file to upload'}
              </Typography>
              <Typography variant="caption">Max size: {maxSizeMb}MB</Typography>
            </>
          )}
        </>
      )}

      {previews.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={2} mt={1}>
          {previews.map((preview, index) => (
            <Box
              key={`${preview.name}-${index}`}
              position="relative"
              width={previewWidth}
              sx={{
                cursor: viewOnly ? 'pointer' : 'default',
              }}
              onClick={() => openPreview(preview)}
            >
              {!viewOnly && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeFile(index)
                    onRemove?.(preview)
                  }}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    bgcolor: '#fff',
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}

              {preview.type === 'image' && (
                <Box
                  component="img"
                  src={preview.url}
                  alt={preview.name}
                  sx={{
                    width: '100%',
                    height: previewWidth,
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                />
              )}

              {preview.type === 'pdf' && (
                <Box
                  component="a"
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    width: '100%',
                    height: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderRadius: 1,
                    textDecoration: 'none',
                    p: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {preview.name}
                  </Typography>
                </Box>
              )}

              {preview.type === 'other' && (
                <Typography variant="caption">{preview.name}</Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      <CustomModal
        isOpen={previewOpen}
        handleClose={closePreview}
        title={activePreview?.name}
        width="50vw"
      >
        <Box mt={4}>
          {activePreview?.type === 'image' && (
            <Box
              component="img"
              src={activePreview.url}
              alt={activePreview.name}
              sx={{
                width: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          )}

          {activePreview?.type === 'pdf' && (
            <Box
              component="iframe"
              src={activePreview.url}
              width="100%"
              height="600px"
              style={{ border: 'none' }}
            />
          )}

          {activePreview?.type === 'other' && (
            <Typography>Preview not available for this file type.</Typography>
          )}
        </Box>

        <DialogActions>
          <Button
            component="a"
            href={activePreview?.url}
            download
            target="_blank"
            rel="noopener noreferrer"
          >
            Download
          </Button>
        </DialogActions>
      </CustomModal>

      {error && (
        <Typography color="error" mt={1}>
          {error}
        </Typography>
      )}
    </Paper>
  )
}
